import React, { useState } from "react";
import { usePageSEO } from "@/hooks/usePageSEO";
import { Link } from "react-router-dom";
import { useTheme } from "@/contexts/ThemeContext";
import { Layers, Play, ArrowRight } from "lucide-react";
import EditorialHeader from "@/components/editorial/EditorialHeader";
import EditorialFooter from "@/components/editorial/EditorialFooter";
import EditorialGridLayout from "@/components/editorial/EditorialGridLayout";
import EditorialSectionDivider from "@/components/editorial/EditorialSectionDivider";
import GridLinesWrapper from "@/components/editorial/GridLinesWrapper";
import AnimatedBorderPill from "@/components/editorial/AnimatedBorderPill";
import BentoFeaturesGrid from "@/components/editorial/BentoFeaturesGrid";
import EditorialPlatformSection from "@/components/editorial/EditorialPlatformSection";
import EditorialMultiPlatformSection from "@/components/editorial/EditorialMultiPlatformSection";
import EditorialStatsBar from "@/components/editorial/EditorialStatsBar";
import EditorialTrialCTA from "@/components/editorial/EditorialTrialCTA";
import EditorialFAQ from "@/components/editorial/EditorialFAQ";
import YourStoryAppSection from "@/components/sections/YourStoryAppSection";
import VideoPopup from "@/components/VideoPopup";
import FeaturesHeroBento from "@/components/features/FeaturesHeroBento";
import PillarsOfProfitSection from "@/components/editorial/PillarsOfProfitSection";
import CompactTestimonialStrip from "@/components/editorial/CompactTestimonialStrip";
import EditorialEngageSection from "@/components/editorial/EditorialEngageSection";
import EditorialAnalyzeSection from "@/components/editorial/EditorialAnalyzeSection";
import EditorialAutomateSection from "@/components/editorial/EditorialAutomateSection";
import FeaturesVideoShowcaseSection from "@/components/editorial/FeaturesVideoShowcaseSection";

// Testimonial data for visual breaks
const testimonials = [
  {
    id: 1,
    videoUrl: "https://kpaixcycljzsxxriyzjh.supabase.co/storage/v1/object/public/video-content/YourStory%20Testimonial%20Style%20UGC%20-%20Linda%20Vittoria%20(1).mp4",
    thumbnail: "https://kpaixcycljzsxxriyzjh.supabase.co/storage/v1/object/public/images/Screenshot%202025-10-23%20211814.png"
  },
  {
    id: 2,
    videoUrl: "https://kpaixcycljzsxxriyzjh.supabase.co/storage/v1/object/public/video-content/Story%20Marketing.mp4",
    thumbnail: "https://kpaixcycljzsxxriyzjh.supabase.co/storage/v1/object/public/images/Screenshot%202025-10-25%20202529.png"
  },
  {
    id: 3,
    videoUrl: "https://kpaixcycljzsxxriyzjh.supabase.co/storage/v1/object/public/video-content/Time-Saving%20Platform%20Empowerment%20Story.mp4",
    thumbnail: "https://kpaixcycljzsxxriyzjh.supabase.co/storage/v1/object/public/images/Screenshot%202025-10-22%20210154.png"
  },
  {
    id: 4,
    videoUrl: "https://kpaixcycljzsxxriyzjh.supabase.co/storage/v1/object/public/video-content/pro%20vision%20ver1.mp4",
    thumbnail: "https://kpaixcycljzsxxriyzjh.supabase.co/storage/v1/object/public/images/Screenshot%202025-10-22%20154250.png"
  },
  {
    id: 5,
    videoUrl: "https://kpaixcycljzsxxriyzjh.supabase.co/storage/v1/object/public/video-content/Fiverr_Story%20(1).mp4",
    thumbnail: "https://kpaixcycljzsxxriyzjh.supabase.co/storage/v1/object/public/images/Screenshot%202025-10-22%20205432.png"
  },
  {
    id: 6,
    videoUrl: "https://kpaixcycljzsxxriyzjh.supabase.co/storage/v1/object/public/video-content/Story_Marketing%20(2).mp4",
    thumbnail: "https://kpaixcycljzsxxriyzjh.supabase.co/storage/v1/object/public/images/Screenshot%202025-10-25%20202907.png"
  }
];

const HomeExperimentalContent = () => {
  const { theme } = useTheme();
  const [isVideoOpen, setIsVideoOpen] = useState(false);

  // Theme-aware colors
  const bgColor = theme === 'dark' ? '#111111' : '#f4f2f1';
  const heroBg = theme === 'dark' ? '#111111' : 'linear-gradient(to bottom, #FFFFFF 0%, #f4f2f1 100%)';
  const headingColor = theme === 'dark' ? '#FFFFFF' : '#111111';
  const textColor = theme === 'dark' ? '#AAAAAA' : '#555555';
  const strongColor = theme === 'dark' ? '#FFFFFF' : '#111111';
  const iconColor = theme === 'dark' ? '#AAAAAA' : '#555555';
  const badgeBgColor = theme === 'dark' ? '#1A1A1A' : '#FFFFFF';
  const badgeBorderGradient = theme === 'dark' 
    ? 'linear-gradient(90deg, transparent 0%, transparent 40%, rgba(255,255,255,0.6) 50%, transparent 60%, transparent 100%)'
    : 'linear-gradient(90deg, transparent 0%, transparent 40%, rgba(0,0,0,0.6) 50%, transparent 60%, transparent 100%)';
  const badgeBorderColor = theme === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)';
  
  // Button colors
  const watchDemoBg = theme === 'dark' ? '#1A1A1A' : '#FFFFFF';
  const watchDemoText = theme === 'dark' ? '#FFFFFF' : '#111111';
  const watchDemoBorder = theme === 'dark' ? '#333333' : '#CCCCCC';
  const startFreeBg = theme === 'dark' ? '#FFFFFF' : '#111111';
  const startFreeText = theme === 'dark' ? '#111111' : '#FFFFFF';
  const startFreeHoverBg = theme === 'dark' ? '#E5E5E5' : '#333333';

  const benefits = [
    'CRM & Pipelines',
    'AI Agents',
    'Funnels & Websites',
    'Email & SMS',
    'Automations',
    'Social Media',
  ];

  return (
    <EditorialGridLayout>
      <div className="min-h-screen overflow-x-hidden" style={{ backgroundColor: bgColor }}>
        {/* V3 Editorial Header */}
        <EditorialHeader />

        {/* Hero Section - AI Agents Style Layout */}
        <GridLinesWrapper>
          <section 
            className="relative pt-28 sm:pt-56 pb-24 px-6"
            style={{ 
              background: heroBg,
            }}
          >
            <div className="max-w-6xl mx-auto">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                {/* Left Content */}
                <div>
                  {/* Badge with animated border - uses stable global CSS classes */}
                  <AnimatedBorderPill text="Features" icon={Layers} className="mb-6" />

                  <h1 
                    className="text-3xl lg:text-4xl font-bold tracking-tight mb-4"
                    style={{ color: headingColor }}
                  >
                    Storily Solves Real World Problems For Business Owners.
                  </h1>

                  <p 
                    className="text-sm mb-8"
                    style={{ color: textColor }}
                  >
                    <strong style={{ color: strongColor }}>Let's be honest:</strong> Most software just adds more work to your plate.<br /><br />Storily is here to kill the "Frankenstein" of scattered apps & manual mess so you can finally get back to actually growing your business.
                  </p>


                  {/* CTA Buttons */}
                  <div className="flex flex-wrap items-center gap-3">
                    <button 
                      onClick={() => setIsVideoOpen(true)}
                      className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
                      style={{ 
                        backgroundColor: watchDemoBg,
                        color: watchDemoText,
                        border: `1px solid ${watchDemoBorder}`,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.boxShadow = theme === 'dark' 
                          ? 'inset 0 0 0 1px #FFFFFF' 
                          : 'inset 0 0 0 1px #000000';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      <Play className="w-3.5 h-3.5" fill="currentColor" />
                      Watch Demo
                    </button>
                    <Link 
                      to="/pricing#pricing-section"
                      className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 hover:scale-[1.02] hover:shadow-md active:scale-[0.98]"
                      style={{ 
                        backgroundColor: startFreeBg,
                        color: startFreeText,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = startFreeHoverBg;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = startFreeBg;
                      }}
                    >
                      Start for free
                      <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                  </div>
                </div>

                {/* Right Visual - Mobile: contained, Desktop: overflow visible */}
                <div className="block sm:hidden overflow-hidden">
                  <FeaturesHeroBento />
                </div>
                <div className="hidden sm:block overflow-visible">
                  <FeaturesHeroBento />
                </div>
              </div>
            </div>
          </section>
        </GridLinesWrapper>

        <EditorialSectionDivider />

        {/* Video Showcase Section - Capture Leads. Close Clients. Zero Friction. */}
        <GridLinesWrapper className="bg-white dark:bg-[#111111]">
          <FeaturesVideoShowcaseSection
            headline="Capture Leads. Close Clients. Zero Friction."
            subheadline="Stop chasing prospects. Automate your entire client journey from introduction to invoice."
          />
        </GridLinesWrapper>

        <EditorialSectionDivider />

        {/* Kill the Frankenstein Table / Stats Bar */}
        <GridLinesWrapper>
          <EditorialStatsBar />
        </GridLinesWrapper>

        <EditorialSectionDivider />

        {/* Build Section - WOW Your Window Shoppers */}
        <GridLinesWrapper>
          <EditorialEngageSection />
        </GridLinesWrapper>

        <EditorialSectionDivider />

        {/* AI Agents Section */}
        <GridLinesWrapper>
          <EditorialAnalyzeSection />
        </GridLinesWrapper>

        <EditorialSectionDivider />

        {/* Inbox Section */}
        <GridLinesWrapper>
          <EditorialAutomateSection />
        </GridLinesWrapper>

        <EditorialSectionDivider />

        {/* Pillars of Profit Section */}
        <GridLinesWrapper>
          <PillarsOfProfitSection />
        </GridLinesWrapper>

        {/* Visual Break: Video Testimonial Strip */}
        <CompactTestimonialStrip testimonials={testimonials} direction="left" />

        <EditorialSectionDivider />

        {/* Bento Features Grid */}
        <GridLinesWrapper>
          <BentoFeaturesGrid />
        </GridLinesWrapper>

        <EditorialSectionDivider />

        {/* AI Platform Section */}
        <GridLinesWrapper>
          <EditorialPlatformSection />
        </GridLinesWrapper>

        <EditorialSectionDivider />

        {/* FAQ Section */}
        <GridLinesWrapper>
          <EditorialFAQ />
        </GridLinesWrapper>

        <EditorialSectionDivider />

        {/* Trial CTA */}
        <GridLinesWrapper>
          <EditorialTrialCTA />
        </GridLinesWrapper>

        <EditorialSectionDivider />

        {/* Mobile App Section */}
        <YourStoryAppSection />

        <EditorialSectionDivider />

        {/* V3 Editorial Footer */}
        <EditorialFooter />
      </div>

      {/* Video Popup */}
      <VideoPopup 
        isOpen={isVideoOpen} 
        onClose={() => setIsVideoOpen(false)} 
      />
    </EditorialGridLayout>
  );
};

const HomeExperimental = () => {
  usePageSEO({
    title: 'Features | Story Marketing Group',
    description: 'One Platform. Zero Bloat. Infinite Scale. Replace 15+ scattered tools with one powerful AI-powered marketing platform. Save thousands yearly.',
    canonical: 'https://storily.io/features',
    keywords: 'marketing automation, lead management, business marketing platform, all-in-one marketing'
  });

  // ThemeProvider is global in App.tsx - no local wrapper needed
  return <HomeExperimentalContent />;
};

export default HomeExperimental;
